import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { isValidPhone } from "@njbsoft/utils";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { useMemberDetail, useUpdateMember } from "@/hooks/use-member-detail";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import {
  MemberIdentityCard,
  type IdentityDraft,
} from "@/components/org-members/member-identity-card";
import {
  MemberEmploymentCard,
  type EmploymentDraft,
  type MetadataRow,
} from "@/components/org-members/member-employment-card";
import { MemberGroupsRoster } from "@/components/org-members/member-groups-roster";
import { MemberCredentialsCard } from "@/components/org-members/member-credentials-card";
import { MemberEffectiveMatrix } from "@/components/org-members/member-effective-matrix";
import { PageHeader, Breadcrumbs, Button, Card, PageSkeleton, Badge } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/org/settings/members/$memberId")({
  component: MemberDetailRoute,
});

function MemberDetailRoute() {
  const { memberId } = Route.useParams();
  return <MemberDetailView memberId={memberId} />;
}

type OrgMemberProfile = components["schemas"]["OrgMemberProfile"];

interface FieldErrors {
  supervisor?: string;
  employeeId?: string;
  phone?: string;
}

/**
 * Two-column org-admin member-detail surface. LEFT: editable Identity +
 * Employment cards (PATCH via the T2 endpoint, three-state per field). RIGHT:
 * the group-memberships roster (assign/remove reuse the per-group endpoints),
 * the person-keyed credentials card (create/edit ride the credential drawer),
 * and the READ-ONLY effective-permission matrix (the union of the member's
 * groups, derived — edits happen on the Groups surface).
 */
export function MemberDetailView({ memberId }: { memberId: string }) {
  const { t } = useTranslation(["org", "common"]);
  const toasts = useToast();
  const resolvedLocale = useAppStore((s) => s.resolvedLocale);
  const { data, isLoading, isError } = useMemberDetail(memberId);
  const updateMember = useUpdateMember(memberId);

  const member = data?.member;
  useRecentPageTitle(member?.full_name ?? undefined);
  const seededIdRef = useRef<string | null>(null);
  const [identity, setIdentity] = useState<IdentityDraft>({
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [employment, setEmployment] = useState<EmploymentDraft>({
    title: "",
    employee_id: "",
    supervisorProfileId: "",
  });
  const [metadataRows, setMetadataRows] = useState<MetadataRow[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const seedFromMember = useCallback((m: OrgMemberProfile) => {
    setIdentity({
      first_name: m.first_name ?? "",
      last_name: m.last_name ?? "",
      phone: m.phone ?? "",
    });
    setEmployment({
      title: m.title ?? "",
      employee_id: m.employee_id ?? "",
      supervisorProfileId: m.supervisor_profile_id ?? "",
    });
    setMetadataRows(
      Object.entries(m.metadata ?? {}).map(([key, val]) => ({
        key,
        value: typeof val === "string" ? val : JSON.stringify(val),
      })),
    );
    setFieldErrors({});
  }, []);

  // Seed the draft once per loaded member (keyed on id) so a background refetch
  // triggered by a group assign/remove does not clobber in-progress edits.
  useEffect(() => {
    if (member && seededIdRef.current !== member.id) {
      seededIdRef.current = member.id;
      seedFromMember(member);
    }
  }, [member, seedFromMember]);

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (isError || !member) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[
            {
              label: t("settings.tabs.members", { ns: "org" }),
              to: "/org/settings",
              search: { tab: "members" },
            },
          ]}
        />
        <PageHeader title={t("memberDetail.loadFailed")} />
      </div>
    );
  }

  const buildBody = (): components["schemas"]["UpdateMemberRequest"] => {
    const body: components["schemas"]["UpdateMemberRequest"] = {};
    const changed = (draft: string, orig: string | null | undefined) => draft !== (orig ?? "");
    if (changed(identity.first_name, member.first_name))
      body.first_name = identity.first_name || null;
    if (changed(identity.last_name, member.last_name)) body.last_name = identity.last_name || null;
    if (changed(identity.phone, member.phone)) body.phone = identity.phone || null;
    if (changed(employment.title, member.title)) body.title = employment.title || null;
    if (changed(employment.employee_id, member.employee_id))
      body.employee_id = employment.employee_id || null;
    if (changed(employment.supervisorProfileId, member.supervisor_profile_id))
      body.supervisor_profile_id = employment.supervisorProfileId || null;
    const metaObj = Object.fromEntries(
      metadataRows.filter((r) => r.key.trim()).map((r) => [r.key.trim(), r.value]),
    );
    if (JSON.stringify(metaObj) !== JSON.stringify(member.metadata ?? {})) body.metadata = metaObj;
    return body;
  };

  const handleSave = () => {
    if (!isValidPhone(identity.phone)) {
      setFieldErrors({ phone: t("common:validation.invalidPhone") });
      return;
    }
    setFieldErrors({});
    updateMember.mutate(buildBody(), {
      onSuccess: () => toasts.success("org:memberDetail.saveSuccess"),
      onError: (err) => {
        const status = err instanceof ApiError ? err.status : 0;
        if (status === 422) {
          setFieldErrors({ supervisor: t("memberDetail.supervisorCycleError") });
          toasts.error("org:memberDetail.supervisorCycleError");
        } else if (status === 409) {
          setFieldErrors({ employeeId: t("memberDetail.employeeIdConflictError") });
          toasts.error("org:memberDetail.employeeIdConflictError");
        } else {
          toasts.error("org:memberDetail.saveFailed");
        }
      },
    });
  };

  const joined = new Date(member.created_at).toLocaleDateString(resolvedLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("memberDetail.orgCrumb") },
          {
            label: t("settings.tabs.members", { ns: "org" }),
            to: "/org/settings",
            search: { tab: "members" },
          },
          { label: member.full_name },
        ]}
      />
      <div className="mt-4">
        <PageHeader
          title={member.full_name}
          subtitle={`${member.email} · ${t("memberDetail.joinedOn", { date: joined })}`}
          meta={
            data.groups.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                {data.groups.map((g) => (
                  <Badge
                    key={g.id}
                    variant={g.is_system_admin ? "warning" : g.is_preset ? "info" : "default"}
                  >
                    {g.name}
                  </Badge>
                ))}
              </div>
            ) : undefined
          }
          actions={
            <>
              <Button variant="secondary" onClick={() => seedFromMember(member)}>
                {t("memberDetail.cancel")}
              </Button>
              <Button onClick={handleSave} disabled={updateMember.isPending}>
                {t("memberDetail.save")}
              </Button>
            </>
          }
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <MemberIdentityCard
            value={identity}
            onChange={(patch) => {
              setIdentity((prev) => ({ ...prev, ...patch }));
              if ("phone" in patch) setFieldErrors((e) => ({ ...e, phone: undefined }));
            }}
            phoneError={fieldErrors.phone}
          />
          <MemberEmploymentCard
            value={employment}
            onChange={(patch) => {
              setEmployment((prev) => ({ ...prev, ...patch }));
              setFieldErrors({});
            }}
            excludeProfileId={member.profile_id}
            supervisorLabel={member.supervisor_name ?? undefined}
            metadataRows={metadataRows}
            onMetadataChange={setMetadataRows}
            errors={fieldErrors}
          />
        </div>

        <div className="space-y-6">
          <MemberGroupsRoster
            memberId={memberId}
            profileId={member.profile_id}
            groups={data.groups}
          />
          <MemberCredentialsCard profileId={member.profile_id} memberName={member.full_name} />
          <Card title={t("memberDetail.effectiveTitle")}>
            <MemberEffectiveMatrix effective={data.effective} groupCount={data.groups.length} />
          </Card>
        </div>
      </div>
    </div>
  );
}
